import React, { useEffect, useRef, useState } from 'react';
import { css, keyframes } from '@emotion/core';
import { Animation, Button } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import MenuCollapseButton from './MenuCollapseButton';
import UniversalTopbar from './UniversalTopbar';
import UniversalSidebar, { UniversalSidebarContainer, } from './UniversalSidebar';
import { addParamsToUrl, extractParams } from '@zapier/url-utils';
import { useUniversalNavContext } from '../hooks/useUniversalNavContext';
import findAccountsMenuRenderer from '../utils/findAccountsMenuRenderer';
import findNotificationsRenderer from '../utils/findNotificationsRenderer';
import useUnscrollableBody from '../hooks/useUnscrollableBody';
import { UniversalTopbarLinks, UNIVERSAL_NAV_ZINDEX, loginPath, signupPath, } from '../constants';
const slideAnimation = keyframes `
  0% { transform: translateX(-100%); }
  100% { transform: translateX(0); }
`;
const Styles = labelStyles('UniversalNav', {
    root: ({ topbarHeight }) => [
        css `
      position: sticky;
      z-index: ${UNIVERSAL_NAV_ZINDEX};
      top: -${topbarHeight}px;
    `,
    ],
    sidebarCtaButtons: css `
    display: grid;
    grid-template-columns: 1fr fr;
    gap: 10px;
  `,
    forceMenuCollapseButton: css `
    margin-left: 5px;
  `,
    sidebarWrapper: ({ isFixed, isFullWidth, }) => [
        css `
      position: absolute;
      display: flex;
      align-items: stretch;
      // Push beneath UniversalTopbar border so the border is seen.
      margin-top: 1px;
      top: 100%;
      left: 0;
      height: 100vh;
      will-change: height;
      transition: height 300ms linear;
    `,
        isFixed &&
            !isFullWidth &&
            css `
        position: fixed;
        top: 0;
        bottom: 0;
        height: auto;
      `,
        isFullWidth &&
            css `
        // Pull above UniversalTopbar border so the border is hidden.
        margin-top: 0;
        width: 100%;
      `,
        !isFullWidth &&
            css `
        animation: ${slideAnimation} ${Animation.transitionDuration} ease-in-out;
      `,
    ],
});
const copyQueryString = (source, destination) => addParamsToUrl(destination, extractParams(source), true);
const Sidebar = (props) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const isFullWidth = props.size === 'small';
    const isActuallyCollapsed = !isFullWidth &&
        !props.isSidebarForcedOpen &&
        !props.isSidebarControlledByTopbar &&
        isCollapsed;
    const sharedProps = {
        isCollapsed: isActuallyCollapsed,
        isFullWidth,
        isShadowed: !isFullWidth && !props.isSidebarForcedOpen && !isActuallyCollapsed,
        onClick: props.onClick,
        onMouseEnter: () => {
            if (window.matchMedia('(hover: hover)').matches) {
                setIsCollapsed(false);
            }
        },
        onMouseLeave: () => {
            if (window.matchMedia('(hover: hover)').matches) {
                setIsCollapsed(true);
            }
        },
        selectedHref: props.currentUrl,
        skipLinkHref: props.skipLinkHref,
    };
    const shouldRenderLoggedInSidebar = props.isLoggedIn &&
        props.currentAccountId &&
        (props.isOnAppPage || props.shouldRenderTopbarLinks);
    useUnscrollableBody({
        shouldAllowScroll: !isFullWidth && !sharedProps.isShadowed,
    });
    const sidebar = shouldRenderLoggedInSidebar ? (isolateComponent(UniversalSidebarContainer, Object.assign({}, sharedProps, { currentAccountId: props.currentAccountId }))) : (isolateComponent(UniversalSidebar, Object.assign({}, sharedProps, { links: props.links, logoAriaLabel: "Go to Zapier homepage", logoHref: "/", renderFooter: () => {
            if (props.size === 'small' && !isActuallyCollapsed) {
                if (!props.isLoggedIn) {
                    return (isolateComponent("div", { css: Styles.sidebarCtaButtons },
                        isolateComponent(Button, { color: "secondary", href: props.loginHref, size: "small" }, "Log in"),
                        isolateComponent(Button, { color: "primary", href: props.signupHref, size: "small" }, "Sign up")));
                }
                if (props.isLoggedIn && !props.isOnAppPage) {
                    return (isolateComponent(Button, { color: "primary-alt", href: "/app/zaps", isFullWidth: true, size: "medium" }, "My Zaps"));
                }
            }
            return null;
        } })));
    const style = { height: props.height };
    return (isolateComponent("div", { css: Styles.sidebarWrapper({
            isFixed: props.isFixed,
            isFullWidth,
        }), style: style }, sidebar));
};
export const UniversalNav = (props) => {
    const { canTopbarFitBackToAppButton, canForceSidebarOpen, currentAccountId, currentUrl, isLoadingLoggedIn, isLoggedIn, isOnAppPage, isSearchFullWidth, isSidebarForcedOpen, isSidebarControlledByTopbar, isSidebarOpenedByTopbar, onToggleSidebarVisibility, setIsSidebarForcedOpen, setIsSidebarOpenedByTopbar, setIsSidebarRendered, shouldRenderSearch, shouldRenderSidebar, shouldRenderTopbarLinks, topbarHeight, topbarSize, } = useUniversalNavContext();
    useEffect(() => {
        setIsSidebarRendered(shouldRenderSidebar);
        return () => {
            setIsSidebarRendered(false);
        };
    }, [setIsSidebarRendered, shouldRenderSidebar]);
    const linksToRender = UniversalTopbarLinks;
    const loginHref = copyQueryString(currentUrl, loginPath);
    const signupHref = copyQueryString(currentUrl, signupPath);
    const renderAccountMenu = findAccountsMenuRenderer({
        canTopbarFitBackToAppButton,
        currentAccountId,
        currentUrl,
        isLoadingLoggedIn,
        isLoggedIn,
        loginHref,
        signupHref,
    });
    const renderNotifications = findNotificationsRenderer({
        currentUrl,
        isLoggedIn,
        renderNotifications: props.renderNotifications,
    });
    const renderSearchForm = shouldRenderSearch ? undefined : () => undefined;
    const [bottom, setBottom] = useState(Infinity);
    const navRef = useRef(null);
    useEffect(() => {
        const setNavBottom = () => {
            if (navRef.current) {
                setBottom(navRef.current.getBoundingClientRect().bottom);
            }
        };
        setNavBottom();
        document.addEventListener('scroll', setNavBottom, { capture: true });
        return () => {
            document.removeEventListener('scroll', setNavBottom, { capture: true });
        };
    }, [topbarHeight, setBottom]);
    const closeSidebar = () => {
        setIsSidebarOpenedByTopbar(false);
    };
    const closeSidebarOnTriggerClick = (e) => {
        const target = e.target;
        const trigger = target.closest('a, button, label, [role="button"]');
        if (isSidebarOpenedByTopbar && !!trigger) {
            closeSidebar();
        }
    };
    const renderSidebar = (topbarProps) => {
        const buttonLabel = isSidebarForcedOpen ? 'Close Sidebar' : 'Open Sidebar';
        const buttonIcon = isSidebarForcedOpen ? 'formX' : 'navMenu';
        const toggleSidebarVisibilityButton = (() => {
            if (onToggleSidebarVisibility) {
                return (isolateComponent(MenuCollapseButton, { ariaExpanded: isSidebarOpenedByTopbar, icon: isSidebarOpenedByTopbar ? 'formX' : 'navMenu', label: "Menu", onClick: onToggleSidebarVisibility }));
            }
            if (canForceSidebarOpen) {
                return (isolateComponent("div", { css: Styles.forceMenuCollapseButton },
                    isolateComponent(MenuCollapseButton, { ariaExpanded: isSidebarForcedOpen, icon: buttonIcon, label: buttonLabel, onClick: () => setIsSidebarForcedOpen(!isSidebarForcedOpen) })));
            }
            return null;
        })();
        const sidebar = shouldRenderSidebar && (isolateComponent(Sidebar, { currentAccountId: currentAccountId, currentUrl: currentUrl, height: `calc(100vh - ${Math.max(0, bottom)}px)`, isFixed: bottom <= 0, isLoggedIn: isLoggedIn, isOnAppPage: isOnAppPage, isSidebarControlledByTopbar: isSidebarControlledByTopbar, isSidebarForcedOpen: isSidebarForcedOpen, links: linksToRender, loginHref: loginHref, onClick: closeSidebarOnTriggerClick, shouldRenderTopbarLinks: shouldRenderTopbarLinks, signupHref: signupHref, size: topbarProps.size, skipLinkHref: props.skipLinkHref }));
        return ((toggleSidebarVisibilityButton || sidebar) && (isolateComponent(React.Fragment, null,
            toggleSidebarVisibilityButton,
            sidebar)));
    };
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
        setHasMounted(true);
    }, []);
    return (isolateComponent("div", { css: Styles.root({ topbarHeight }), key: hasMounted ? 'has-mounted' : 'has-not-mounted', ref: navRef },
        isolateComponent(UniversalTopbar, { height: topbarHeight, isSearchFullWidth: isSearchFullWidth, links: shouldRenderTopbarLinks ? linksToRender : [], onClick: closeSidebarOnTriggerClick, onClickOutside: closeSidebar, renderAccountMenu: renderAccountMenu, renderNotifications: renderNotifications, renderSearchForm: renderSearchForm, renderSidebar: renderSidebar, selectedHref: currentUrl, size: topbarSize, skipLinkHref: props.skipLinkHref })));
};
UniversalNav.defaultProps = {
    renderNotifications: () => null,
};
//# sourceMappingURL=UniversalNav.js.map
// @zapier/universal-layout@5.12.61 (4902ab0c1)
